import CONFIG from 'shared/constants/config';

export const getUniversalAddition: (arg: number, arg2: string[]) => string = (
  num,
  array,
) => {
  if ((num > 10 && num < 21) || (num % 100 > 10 && num % 100 < 21)) {
    return array[2];
  }
  switch (num % 10) {
    case 1:
      return array[0];
    case 2:
    case 3:
    case 4:
      return array[1];
    default:
      return array?.[3] || array[2];
  }
};

export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );
};

export const isMobileResolution = (): boolean => {
  return window.innerWidth < 680;
};

export const getRankIcon = (position: number) => {
  switch (position) {
    case 1:
      return '🥇';
    case 2:
      return '🥈';
    case 3:
      return '🥉';
    default:
      return '#' + position;
  }
};

export const getBackgroundAvatar = (name: string) => {
  const colorMap = [
    '#40C4FF',
    '#FFA726',
    '#E91E63',
    '#4CAF50',
    '#FF5722',
    '#673AB7',
    '#795548',
    '#607D8B',
    '#009688',
    '#9C27B0',
    '#2196F3',
    '#03A9F4',
    '#00BCD4',
    '#8BC34A',
    '#CDDC39',
    '#FFEB3B',
    '#FF9800',
    '#9E9E9E',
    '#9FA8DA',
    '#3F51B5',
    '#64B5F6',
    '#1976D2',
    '#90CAF9',
    '#3949AB',
    '#303F9F',
    '#536DFE',
    '#311B92',
    '#4527A0',
    '#512DA8',
    '#9575CD',
    '#7E57C2',
    '#BA68C8',
    '#CE93D8',
    '#EA80FC',
    '#EC407A',
    '#AB47BC',
    '#B388FF',
    '#651FFF',
    '#6200EA',
    '#AA00FF',
    '#AD1457',
    '#880E4F',
    '#D50000',
    '#F44336',
    '#EF5350',
    '#E53935',
    '#FB8C00',
    '#FF7043',
    '#FE5722',
    '#757575',
    '#616161',
    '#424242',
    '#212121',
    '#263238',
  ];
  const firstLetter = name.toLowerCase().charAt(0);
  const index = firstLetter.charCodeAt(0) % colorMap.length;
  return colorMap[index];
};

export function safeJsonParse(jsonString) {
  try {
    // Пытаемся распарсить JSON
    const parsedData = JSON.parse(jsonString);
    return { success: true, data: parsedData };
  } catch (error) {
    // В случае ошибки возвращаем объект с информацией об ошибке
    return { success: false, error: error.message };
  }
}

export function consoleDev(...args: unknown[]) {
  if (CONFIG.IS_DEV) {
    console.log(...args);
  }
}

export const formatPhoneNumber = phoneNumber => {
  return phoneNumber.replace(
    /^(\+\d)(\d{3})(\d{3})(\d{2})(\d{2})$/,
    '$1 $2 $3-$4-$5',
  );
};

export const addSpaces = (nStr: string | number): string => {
  const x = String(nStr).split('.');
  let x1 = x[0];
  const x2 = x.length > 1 ? `.${x[1]}` : '';
  const rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, `$1 $2`);
  }
  return x1 + x2;
};

export const formatMoney = (amount: number): string => {
  if (!amount) return '';
  return `${addSpaces(amount)} ₽`;
};

export const formatDate = (date: string): string => {
  return new Date(date).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

interface DeviceInfo {
  platform:
    | 'ios'
    | 'android'
    | 'tdesktop'
    | 'web'
    | 'macos'
    | 'windows'
    | 'linux'
    | 'unknown';
  deviceType: 'mobile' | 'desktop' | 'tablet' | 'unknown';
  deviceModel: string | null;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
}

/**
 * Определяет информацию об устройстве пользователя
 * Использует tgWebAppPlatform из Telegram SDK и navigator.userAgent
 * @param tgWebAppPlatform - Платформа из launchParams.tgWebAppPlatform
 */
export const getDeviceInfo = (tgWebAppPlatform?: string): DeviceInfo => {
  const userAgent = navigator.userAgent;
  let platform: DeviceInfo['platform'] = 'unknown';
  let deviceModel: string | null = null;
  let deviceType: DeviceInfo['deviceType'] = 'unknown';

  try {
    // Определяем платформу из Telegram SDK
    if (tgWebAppPlatform) {
      platform = tgWebAppPlatform as DeviceInfo['platform'];
    } else {
      // Fallback на userAgent если SDK не доступен
      if (/iPhone|iPad|iPod/i.test(userAgent)) {
        platform = 'ios';
      } else if (/Android/i.test(userAgent)) {
        platform = 'android';
      } else if (/Mac/i.test(userAgent)) {
        platform = 'macos';
      } else if (/Win/i.test(userAgent)) {
        platform = 'windows';
      } else if (/Linux/i.test(userAgent)) {
        platform = 'linux';
      }
    }

    // Определяем тип устройства
    if (/iPhone|iPod|Android.*Mobile|Mobile.*Android/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/iPad|Android(?!.*Mobile)/i.test(userAgent)) {
      deviceType = 'tablet';
    } else if (
      platform === 'tdesktop' ||
      platform === 'macos' ||
      platform === 'windows' ||
      platform === 'linux'
    ) {
      deviceType = 'desktop';
    } else if (platform === 'ios' || platform === 'android') {
      deviceType = 'mobile';
    }

    // Определяем модель устройства для iOS
    if (platform === 'ios') {
      // iPhone модели определяются по userAgent
      // В WebView Telegram userAgent может быть ограничен, но попробуем
      const iphoneMatch = userAgent.match(/iPhone(\d+,\d+)?/i);
      if (iphoneMatch) {
        // Пытаемся определить модель по OS версии и другим признакам
        // iPhone 14 и 16 имеют разные версии iOS
        const osVersionMatch = userAgent.match(/OS (\d+)[._](\d+)/i);
        if (osVersionMatch) {
          const majorVersion = parseInt(osVersionMatch[1], 10);
          // iOS 16+ обычно на iPhone 14 и новее
          // iOS 17+ обычно на iPhone 15 и новее
          // iOS 18+ обычно на iPhone 16 и новее
          if (majorVersion >= 18) {
            deviceModel = 'iPhone 16 или новее';
          } else if (majorVersion >= 17) {
            deviceModel = 'iPhone 15 или новее';
          } else if (majorVersion >= 16) {
            deviceModel = 'iPhone 14 или новее';
          } else {
            deviceModel = 'iPhone (старая модель)';
          }
        } else {
          deviceModel = 'iPhone';
        }
      } else if (/iPad/i.test(userAgent)) {
        deviceModel = 'iPad';
      } else if (/iPod/i.test(userAgent)) {
        deviceModel = 'iPod';
      }
    }

    // Определяем модель устройства для Android
    if (platform === 'android') {
      // Пытаемся найти модель в userAgent
      // Формат может быть разным: "SM-G991B" (Samsung), "Pixel 7", и т.д.
      const androidModelMatch = userAgent.match(/Android.*?;\s*([^)]+)\)/i);
      if (androidModelMatch) {
        const modelInfo = androidModelMatch[1].trim();
        // Убираем лишние данные
        deviceModel = modelInfo.split(';')[0].trim();
      } else {
        deviceModel = 'Android устройство';
      }
    }
  } catch (error) {
    console.error('Ошибка при определении устройства:', error);
    // Fallback на базовое определение через userAgent
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      platform = 'ios';
      deviceType = /iPad/i.test(userAgent) ? 'tablet' : 'mobile';
    } else if (/Android/i.test(userAgent)) {
      platform = 'android';
      deviceType = /Mobile/i.test(userAgent) ? 'mobile' : 'tablet';
    }
  }

  return {
    platform,
    deviceType,
    deviceModel,
    isIOS: platform === 'ios',
    isAndroid: platform === 'android',
    isMobile:
      deviceType === 'mobile' || platform === 'ios' || platform === 'android',
  };
};
